import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Dropdown,
  FormInput,
  Icon,
  Label,
  LabelType,
} from '../../_ui';

import { ExitDeleteRemoveClose } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { Invitee } from '../../../stores/types/invites.interface';
import { UserRole } from '../../../stores/types/usergroups.interface';
import { ValidationType } from '../../_ui/Input.component';
import { validateEmail } from '../../_ui/modal/utils';

/** All roles available in the invite role dropdown (order matches dropdown list) */
const INVITE_ROLE_OPTIONS: UserRole[] = [
  UserRole.ACCOUNTING,
  UserRole.ADMIN,
  UserRole.FACILITY_MANAGER,
  UserRole.TECHNICIAN,
  UserRole.READ_ONLY,
];

interface IProps {
  invitees: Invitee[];
  setInvitees: (invitees: Invitee[]) => void;
}

export const InviteUser = ({ invitees, setInvitees }: IProps) => {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');

  const onButtonValidation = useMemo(() => {
    return (
      validateEmail(email) && !invitees.some((invitee) => invitee.email === email.toLowerCase())
    );
  }, [invitees, email]);

  const handleAddUsers = () => {
    setEmail('');
    setInvitees([...invitees, { email: email.toLowerCase(), role: UserRole.ADMIN }]);
  };

  const removeUsers = (userEmail: string) => {
    const filterUsers = invitees.filter((invitee: Invitee) => invitee.email !== userEmail);
    setInvitees(filterUsers);
  };

  return (
    <>
      <div>
        <Label
          text={t('selected_user_receive_email_text')}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
        />
        <Label
          className='mt-[16px]'
          text={t('user_do_not_have_account_text')}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
        />
      </div>
      <div className='mt-[16px] mb-[24px]'>
        <div className='flex flex-row'>
          <div className='w-[374px]'>
            <FormInput
              inputId='user_to_invite'
              label={t('user_to_invite')}
              placeholder={t('invite_email_holder')}
              showErrorIcon
              onChange={(event) => setEmail(event.target.value)}
              validationType={ValidationType.EMAIL}
              width='100%'
              props={{ value: email }}
              dataTestId='inviteEmailInput'
            />
          </div>
          <div className='ml-[16px] h-[40px] w-[118px] mt-[32px]'>
            <Button
              label={t('add_to_invite')}
              type={ButtonType.PRIMARY}
              disabled={!onButtonValidation}
              onClick={handleAddUsers}
              size={ButtonSize.SMALL_FULL}
              dataTestId='addInviteEmailButton'
            />
          </div>
        </div>
      </div>

      <div className='mt-[24px]'>
        <div className='grid items-center border-b pb-2 text-xs font-semibold text-gray-700 gap-4 grid-cols-[2fr_3fr_60px]'>
          <div>
            {' '}
            <Label text={t('company_email')} type={LabelType.LABEL_XS} color={ColorType.GREY6} />
          </div>
          <div>
            {' '}
            <Label
              text={t('company_invite_role')}
              type={LabelType.LABEL_XS}
              color={ColorType.GREY6}
            />
          </div>
          <div>
            <Label
              text={t('company_remove_user')}
              type={LabelType.LABEL_XS}
              color={ColorType.GREY6}
            />
          </div>
        </div>
        {invitees.length === 0 ? (
          <div className='py-4'>
            <Label
              text={t('invite_users_empty_state')}
              type={LabelType.BODY3}
              color={ColorType.GREY5}
            />
          </div>
        ) : (
          invitees.map((invitee: Invitee, idx: number) => (
            <div
              key={invitee.email}
              className='grid grid-cols-[2fr_3fr_60px] gap-4 items-center py-2'
            >
              <p className='truncate text-sm font-normal font-inter text-black'>{invitee.email}</p>

              <Dropdown
                placeholder={t('company_invite_role') as string}
                items={INVITE_ROLE_OPTIONS.map((role) => ({
                  id: role,
                  label: (t(`user_role_${role.toLowerCase()}`) as string) || role,
                  selected: invitee.role === role,
                }))}
                label='label'
                onItemClick={(item: { id: string }) => {
                  const newInvitees = [...invitees];
                  newInvitees[idx].role = item.id as UserRole;
                  setInvitees([...newInvitees]);
                }}
                headerWidth='100%'
                headerHeight='44px'
                contentDivWidth={200}
                contentDivHeight={320}
                placement='top-start'
                headerClassName='bg-grey1 border-grey3 rounded h-[44px] min-w-[80px]'
                headerHighLightClassName='bg-grey1 border-grey3 rounded h-[44px] min-w-[80px]'
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                dataTestId='inviteRoleDropdown'
              />

              <div>
                <button
                  type='button'
                  className='text-gray-500 hover:text-red-600 transition-colors'
                  onClick={() => removeUsers(invitee.email)}
                  data-testid={`remove-user-${invitee.email}`}
                  aria-label={t('remove_user')}
                >
                  <Icon
                    size={IconSize.SIZE_24x24}
                    src={ExitDeleteRemoveClose}
                    color={ColorType.BLACK}
                    alt={t('icon_alt_info')}
                  />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};
